import classNames from "classnames";
import { Field, useField } from "formik";
import { omit } from "lodash";

import { Accession, Case, Clinic, UserName } from "pwapi";

import useClinicFields, { isMRNRequired } from "shared/hooks/useClinicFields";
import useLocationInputs from "shared/hooks/useLocationInputs";
import { formatPhone, formatSSN } from "shared/utils/patientUtils";
import {
  getValueFromObject,
  requiredError,
} from "shared/utils/validationUtils";

import { Input } from "ui/accessioning/AccessionForm";
import useUserInformationInputs from "ui/CaseFormInputs/useUserInformationInputs";

import FormHeaders from "./FormHeaders";

const PatientForm = ({
  caseType,
  billingType,
  onHandlePatchAccessionPatient,
  onHandlePatchAccessionAddress,
  patientInputPrefix,
  displaySubmit,
  onHandleDiscardChanges,
  dirty,
  values,
  currClinic,
  orderingDoctor,
  initialAddressFilled,
  errors = {},
  touched = {},
  warnings = {},
  thirdPartyRequireLocationFields = false,
  requireStateField = false,
  tcOnly = false,
}: {
  caseType?: Accession["case_type"];
  billingType?: Case.billing_type | null;
  onHandlePatchAccessionPatient?: Function;
  onHandlePatchAccessionAddress?: Function;
  patientInputPrefix?: string;
  displaySubmit?: boolean;
  dirty?: boolean;
  onHandleDiscardChanges?: Function;
  values: Accession["patient"];
  currClinic?: Clinic;
  orderingDoctor?: UserName;
  initialAddressFilled?: boolean;
  errors?: any;
  touched?: any;
  warnings?: { [key: string]: string };
  thirdPartyRequireLocationFields?: boolean;
  requireStateField?: boolean;
  tcOnly?: boolean;
}) => {
  const patientInputs = useUserInformationInputs(
    "patient",
    false,
    patientInputPrefix || undefined
  );
  const validationPrefix = patientInputPrefix ? "patient." : "";
  const addressInputs = useLocationInputs("patient_location", "address");

  const { address, ...patient } = values;

  const requireLocationFields =
    initialAddressFilled || thirdPartyRequireLocationFields
      ? billingType === Case.billing_type.GLOBAL
      : false;
  const requireMRNField =
    tcOnly || caseType === Accession.case_type.CONSULT
      ? false
      : isMRNRequired(
          useClinicFields({
            clinic: currClinic,
            ordering_doctor_id: orderingDoctor?.id,
          })
        );
  // eslint-disable-next-line
  const [, , { setTouched: setPatientSexTouched }] = useField(
    `${validationPrefix}sex`
  );
  const [, , { setTouched: setPatientAddressStateTouched }] =
    useField("address.state");
  const sexError = getValueFromObject(errors, `${validationPrefix}sex`);

  return (
    <>
      <FormHeaders
        title="Patient Information"
        dirty={dirty}
        displaySubmit={displaySubmit}
        onHandleDiscardChanges={onHandleDiscardChanges}
        errors={errors}
        touched={touched}
        warnings={warnings}
        forms={[
          "address.phone1",
          "address.phone2",
          "address.street",
          "address.zipcode",
          "address.state",
          `${validationPrefix}date_of_birth`,
          `${validationPrefix}first_name`,
          `${validationPrefix}last_name`,
          `${validationPrefix}medical_record_number`,
          "medical_record_number",
          `${validationPrefix}sex`,
        ]}
        formWarnings={[`${validationPrefix}date_of_birth`]}
      />
      <div className="LISForm__section">
        <Input
          {...patientInputs.lastName}
          className="Input--2"
          handleOnBlur={
            onHandlePatchAccessionPatient &&
            (() => {
              onHandlePatchAccessionPatient(patient);
            })
          }
          required
          error={getValueFromObject(errors, `${validationPrefix}last_name`)}
          touched={getValueFromObject(touched, `${validationPrefix}last_name`)}
        />
        <Input
          {...patientInputs.firstName}
          className="Input--2"
          handleOnBlur={
            onHandlePatchAccessionPatient &&
            (() => {
              onHandlePatchAccessionPatient(patient);
            })
          }
          error={getValueFromObject(errors, `${validationPrefix}first_name`)}
          touched={getValueFromObject(touched, `${validationPrefix}first_name`)}
          required
        />
        <Input
          {...patientInputs.middleInitial}
          className="Input--2"
          handleOnBlur={
            onHandlePatchAccessionPatient &&
            (() => {
              onHandlePatchAccessionPatient(patient);
            })
          }
        />
        <Input
          {...patientInputs.ssn}
          className="Input--1"
          handleOnBlur={
            onHandlePatchAccessionPatient &&
            (() => {
              onHandlePatchAccessionPatient(patient);
            })
          }
          formatter={formatSSN}
        />
        <Input
          {...patientInputs.dateOfBirth}
          className="Input--1"
          handleOnBlur={
            onHandlePatchAccessionPatient &&
            (() => {
              onHandlePatchAccessionPatient({
                ...patient,
                date_of_birth: patient.date_of_birth || null,
              });
            })
          }
          error={getValueFromObject(errors, `${validationPrefix}date_of_birth`)}
          touched={getValueFromObject(
            touched,
            `${validationPrefix}date_of_birth`
          )}
          warning={getValueFromObject(
            warnings,
            `${validationPrefix}date_of_birth`
          )}
          required
        />
        <div
          className={classNames(
            "Accession__input",
            "Accession__input--required",
            "Input--1",
            sexError &&
              getValueFromObject(touched, `${validationPrefix}sex`) &&
              !requiredError(sexError) &&
              "Accession__input--error",
            sexError && "Accession__input--submission-check-error"
          )}
        >
          <label>Sex *</label>
          <Field
            {...omit(patientInputs.sex, "defaultOptionLabel", "options")}
            as="select"
            onBlur={() => {
              setPatientSexTouched(true);
              onHandlePatchAccessionPatient &&
                onHandlePatchAccessionPatient(patient);
            }}
            required
          >
            <option value="">{patientInputs.sex.defaultOptionLabel}</option>
            {patientInputs.sex.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        </div>
        <Input
          {...patientInputs.mrn}
          className="Input--3"
          handleOnBlur={
            onHandlePatchAccessionPatient &&
            (() => {
              onHandlePatchAccessionPatient(patient);
            })
          }
          error={getValueFromObject(
            errors,
            `${validationPrefix}medical_record_number`
          )}
          touched={getValueFromObject(
            touched,
            `${validationPrefix}medical_record_number`
          )}
          required={requireMRNField}
        />

        <Input
          {...addressInputs.street}
          className="Input--6"
          handleOnBlur={
            onHandlePatchAccessionAddress &&
            (() => {
              onHandlePatchAccessionAddress(address);
            })
          }
          error={getValueFromObject(errors, "address.street")}
          touched={getValueFromObject(touched, "address.street")}
          required={requireLocationFields}
        />
        <Input
          {...addressInputs.street2}
          className="Input--6"
          handleOnBlur={
            onHandlePatchAccessionAddress &&
            (() => {
              onHandlePatchAccessionAddress(address);
            })
          }
        />
        <Input
          {...addressInputs.city}
          handleOnBlur={
            onHandlePatchAccessionAddress &&
            (() => {
              onHandlePatchAccessionAddress(address);
            })
          }
        />
        <div
          className={classNames("Accession__input", "Input--1", {
            "Accession__input--required": requireStateField,
            "Accession__input--error":
              requireStateField && getValueFromObject(touched, "address.state"),
            "Accession__input--submission-check-error": getValueFromObject(
              errors,
              "address.state"
            ),
          })}
        >
          <label htmlFor={addressInputs.state.id}>
            State{requireStateField && " *"}
          </label>
          <Field
            {...omit(addressInputs.state, "defaultOptionLabel", "options")}
            as="select"
            onBlur={() => {
              setPatientAddressStateTouched(true);
              onHandlePatchAccessionAddress &&
                onHandlePatchAccessionAddress(address);
            }}
            required={requireStateField}
          >
            <option value="">{addressInputs.state.defaultOptionLabel}</option>
            {addressInputs.state.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        </div>
        <Input
          {...addressInputs.zipcode}
          className="Input--2"
          handleOnBlur={
            onHandlePatchAccessionAddress &&
            (() => {
              onHandlePatchAccessionAddress(address);
            })
          }
          error={getValueFromObject(errors, "address.zipcode")}
          touched={getValueFromObject(touched, "address.zipcode")}
          required={requireLocationFields}
        />
        <Input
          {...addressInputs.phone1}
          className="Input--3"
          handleOnBlur={
            onHandlePatchAccessionAddress &&
            (() => {
              onHandlePatchAccessionAddress(address);
            })
          }
          error={getValueFromObject(errors, "address.phone1")}
          touched={getValueFromObject(touched, "address.phone1")}
          required={requireLocationFields}
          formatter={formatPhone}
        />
        <Input
          {...addressInputs.phone2}
          className="Input--3"
          handleOnBlur={
            onHandlePatchAccessionAddress &&
            (() => {
              onHandlePatchAccessionAddress(address);
            })
          }
          formatter={formatPhone}
          error={getValueFromObject(errors, "address.phone2")}
          touched={getValueFromObject(touched, "address.phone2")}
        />
      </div>
    </>
  );
};

export default PatientForm;
